import type { Partner as ApiPartner } from '@/api/partners';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';

/**
 * Rental-specific extra info on partner cards (a wizard step1 PartnerSearchSelect
 * `renderExtra` slotja). Külön fájl: a `PartnerStep` import-lánca (PartnerSearchSelect
 * → ScanIdCardButton → @kgc/tesseract-provider OCR) NEM kell ide, így a komponens
 * izoláltan tesztelhető. Render-win (2026-06-03): a bérlés-előzmény + hűségpont adat
 * MEGVAN (`stats.totalRentals` / `stats.lastRentalDate` / `loyaltyPoints`) — a pultos
 * így látja a visszatérő-ügyfél jelzést partner-választáskor.
 */
export function RentalPartnerExtra({ partner }: { partner: ApiPartner }) {
  const { t } = useTranslation();

  // Defenzív (`stats?`) — egyes API-shape-ek nem adják a stats-ot.
  const totalRentals = partner.stats?.totalRentals;
  const lastRentalDate = partner.stats?.lastRentalDate;

  return (
    <>
      {partner.loyaltyPoints > 1000 && (
        <span className="mt-2 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
          VIP · {partner.loyaltyPoints.toLocaleString('hu-HU')}{' '}
          {t('rental.wizard.loyaltyPointsUnit')}
        </span>
      )}

      {/* Bérlés-előzmény: visszatérő ügyfél jelzése a pultosnak (render-win). */}
      {typeof totalRentals === 'number' && totalRentals > 0 && (
        <div className="mt-2 text-xs text-muted-foreground" data-testid="rental-partner-history">
          {t('rental.wizard.totalRentalsLabel')} {totalRentals}
          {lastRentalDate &&
            ` · ${t('rental.wizard.lastRentalLabel')} ${new Date(lastRentalDate).toLocaleDateString('hu-HU')}`}
        </div>
      )}

      {partner.status === 'warning' && partner.warningNote && (
        <div className="mt-2 rounded bg-orange-50 dark:bg-orange-900/20 px-2 py-1 text-xs text-orange-700 dark:text-orange-300">
          {partner.warningNote}
        </div>
      )}

      {partner.costBearerName && (
        <div
          className={cn(
            'mt-2 rounded bg-blue-50 dark:bg-blue-900/20 px-2 py-1 text-xs text-blue-700 dark:text-blue-300'
          )}
        >
          {t('rental.wizard.costBearerLabel')} {partner.costBearerName}
        </div>
      )}

      {partner.currentBalance !== 0 && (
        <div
          className={cn(
            'mt-2 text-sm font-medium',
            partner.currentBalance < 0
              ? 'text-red-600 dark:text-red-400'
              : 'text-green-600 dark:text-green-400'
          )}
        >
          {t('rental.wizard.balanceLabel')} {partner.currentBalance < 0 ? '-' : '+'}
          {Math.abs(partner.currentBalance).toLocaleString('hu-HU')} Ft
        </div>
      )}
    </>
  );
}
